/** SUNTRONIX ONE — Product Image Enrichment workbench.
 *  TECHNOLOGY → PRODUCT CONTENT → IMAGE ENRICHMENT.
 *  Universal upload (any format, auto-processed) + governed auto-match.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ImageIcon, ImageOff, UploadCloud, Sparkles, ScanSearch, Loader2,
  CheckCircle2, Trash2, Link2, Globe, ShieldCheck, Wand2, RefreshCw, ChevronDown,
  Eraser, X,
} from "lucide-react";

const HEALTH_STYLE = {
  ACTIVE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  DEGRADED: "bg-amber-100 text-amber-800 border-amber-200",
  SETUP_REQUIRED: "bg-slate-100 text-slate-500 border-slate-200",
  ERROR: "bg-rose-100 text-rose-700 border-rose-200",
};

const STATUS_STYLE = {
  VERIFIED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  AUTO_MATCHED: "bg-sky-100 text-sky-800 border-sky-200",
  AI_GENERATED: "bg-violet-100 text-violet-800 border-violet-200",
  REVIEW: "bg-amber-100 text-amber-800 border-amber-200",
  MISSING: "bg-slate-100 text-slate-500 border-slate-200",
};

function Placeholder({ label }) {
  return (
    <div className="w-full h-full grid place-items-center bg-slate-50 text-slate-300"
      data-testid="image-placeholder">
      <div className="flex flex-col items-center gap-1">
        <ImageOff className="h-6 w-6" strokeWidth={1.4} />
        <span className="text-[9px] uppercase tracking-wide text-slate-400 text-center px-1">{label || "No image"}</span>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 flex items-center gap-3">
      <div className={"h-10 w-10 rounded-lg grid place-items-center shrink-0 " + (tone || "bg-slate-100 text-slate-600")}>
        <Icon className="h-5 w-5" strokeWidth={1.6} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold text-slate-900 leading-none">{value ?? "—"}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

function ProductCard({ p, onUpload, onRemove, onSetUrl, onResolve, onAiGenerate, onCleanBg, busy }) {
  const fileRef = useRef(null);
  const bgFileRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const raw = p.image_display_url;
  const url = raw ? (raw.startsWith("http") ? raw : `${API}${raw}`) : null;
  const isPlaceholder = p.image_status === "PLACEHOLDER" || (!p.primary_image?.url && !p.primary_image?.storage_path);
  const isAi = p.image_status === "AI_GENERATED" || p.primary_image?.ai_generated;
  const bgCleaned = p.primary_image?.bg_removed || p.primary_image?.source_type === "AI_BG_REMOVED";
  const handleFiles = (files) => {
    if (files && files[0]) onUpload(p, files[0]);
  };
  const handleBgFiles = (files) => {
    if (files && files[0]) onCleanBg(p, files[0]);
  };
  return (
    <div data-testid={`img-card-${p.oem_part_number || p.id}`}
      className="rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        className={"relative aspect-square cursor-pointer border-b border-slate-100 " + (drag ? "ring-2 ring-sky-400" : "")}
        title="Click or drop an image to upload">
        {busy === p.id && (
          <div className="absolute inset-0 z-10 bg-white/70 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-sky-600" />
          </div>
        )}
        {url ? (
          <img src={url} alt={p.product_name} className="w-full h-full object-contain p-3" data-testid={`img-thumb-${p.id}`} />
        ) : <Placeholder label={`${p.brand || ""} ${p.category || ""}`.trim() || "No image"} />}
        {isAi && url && (
          <span data-testid={`img-ai-badge-${p.id}`}
            className="absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-violet-600 text-white shadow-sm">
            <Sparkles className="h-2.5 w-2.5" /> AI
          </span>
        )}
        {bgCleaned && url && (
          <span data-testid={`img-bgclean-badge-${p.id}`}
            className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-500 text-white shadow-sm">
            <Eraser className="h-2.5 w-2.5" /> BG
          </span>
        )}
        <input ref={fileRef} type="file" accept="image/*,.heic,.heif,.tiff,.tif,.bmp,.avif,.svg" hidden
          data-testid={`img-file-${p.id}`}
          onChange={(e) => handleFiles(e.target.files)} />
        <input ref={bgFileRef} type="file" accept="image/*,.heic,.heif,.tiff,.tif,.bmp,.avif" hidden
          data-testid={`img-bgfile-${p.id}`}
          onChange={(e) => { handleBgFiles(e.target.files); e.target.value = ""; }} />
      </div>
      <div className="p-3 flex-1 flex flex-col gap-1">
        <div className="text-[13px] font-medium text-slate-800 line-clamp-2">{p.product_name}</div>
        <div className="text-[11px] text-slate-400 font-mono">{p.oem_part_number || "—"}</div>
        <div className="mt-1 flex items-center justify-between">
          <Badge className={"text-[10px] border " + (STATUS_STYLE[p.image_status] || STATUS_STYLE.MISSING)}>
            {(p.image_status || "MISSING").replace("_", " ")}
          </Badge>
          <div className="flex items-center gap-1">
            <button onClick={() => onResolve(p)} title="Auto-resolve official image (OEM → Icecat → brand site + AI)"
              data-testid={`img-resolve-${p.id}`} className="p-1.5 rounded hover:bg-indigo-50 text-indigo-500">
              <Wand2 className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => onAiGenerate(p)} title="Generate a representative image with AI (Nano Banana)"
              data-testid={`img-ai-${p.id}`} className="p-1.5 rounded hover:bg-violet-50 text-violet-500">
              <Sparkles className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => bgFileRef.current?.click()} title="Clean background with AI (removes cluttered background → white)"
              data-testid={`img-bgclean-${p.id}`} className="p-1.5 rounded hover:bg-amber-50 text-amber-500">
              <Eraser className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => onSetUrl(p)} title="Set approved image URL"
              data-testid={`img-seturl-${p.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
              <Link2 className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => fileRef.current?.click()} title="Upload image"
              data-testid={`img-upload-${p.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
              <UploadCloud className="h-3.5 w-3.5" />
            </button>
            {!isPlaceholder && url && (
              <button onClick={() => onRemove(p)} title="Remove image"
                data-testid={`img-remove-${p.id}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-500">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SourceHealth({ health }) {
  if (!health) return null;
  return (
    <div data-testid="source-health" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-800">Product Image Source Health</span>
        <span className="text-[11px] text-slate-400">{health.confirmed_domains} confirmed brand domains</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {health.sources?.map((s) => (
          <div key={s.id} data-testid={`health-${s.id}`} className="rounded-lg border border-slate-100 bg-slate-50/60 p-2.5">
            <div className="flex items-center justify-between gap-1">
              <span className="text-[11px] font-medium text-slate-700 leading-tight">{s.name}</span>
            </div>
            <Badge className={"mt-1.5 text-[9px] border " + (HEALTH_STYLE[s.status] || HEALTH_STYLE.SETUP_REQUIRED)}>
              {s.status.replace("_", " ")}
            </Badge>
            <div className="text-[10px] text-slate-400 mt-1 leading-snug line-clamp-2">{s.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandDomains({ domains, onSave, onSuggest, busyId }) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState({});
  if (!domains) return null;
  const val = (r) => drafts[r.id] ?? (r.official_domain || "");
  return (
    <div data-testid="brand-domains" className="rounded-xl border border-slate-200 bg-white">
      <button onClick={() => setOpen((o) => !o)} data-testid="brand-domains-toggle"
        className="w-full flex items-center justify-between px-4 py-3">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Globe className="h-4 w-4 text-slate-500" /> Brand Domain Master
          <span className="text-[11px] font-normal text-slate-400">
            {domains.confirmed}/{domains.total} confirmed · governs the no-credential resolver
          </span>
        </span>
        <ChevronDown className={"h-4 w-4 text-slate-400 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {domains.items?.map((r) => (
            <div key={r.id} data-testid={`brand-row-${r.brand_lc}`}
              className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
              <span className="w-28 text-[13px] font-medium text-slate-700 truncate">{r.brand}</span>
              <Input data-testid={`brand-domain-input-${r.brand_lc}`}
                value={val(r)} placeholder="official-domain.com"
                onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                className="flex-1 min-w-[160px] h-8 text-sm" />
              <Badge className={"text-[10px] border " + (r.confirmed ? HEALTH_STYLE.ACTIVE : HEALTH_STYLE.SETUP_REQUIRED)}>
                {r.confirmed ? "confirmed" : "unset"}
              </Badge>
              {r.suggested_domain && (
                <span className="text-[10px] text-indigo-500">AI: {r.suggested_domain} ({r.suggested_confidence})</span>
              )}
              <button onClick={() => onSuggest(r)} disabled={busyId === r.id} title="AI suggest official domain"
                data-testid={`brand-suggest-${r.brand_lc}`} className="p-1.5 rounded hover:bg-indigo-50 text-indigo-500">
                {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              </button>
              <Button size="sm" variant="secondary" data-testid={`brand-save-${r.brand_lc}`}
                onClick={() => onSave(r, val(r))} className="h-8 text-xs">Confirm</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ImageEnrichment() {
  const [ov, setOv] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [job, setJob] = useState(null);
  const [health, setHealth] = useState(null);
  const [domains, setDomains] = useState(null);
  const [domBusy, setDomBusy] = useState(null);
  const [bgResult, setBgResult] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/product-images/overview"),
      api.get("/product-images/products", { params: { q: q || undefined, status: status || undefined, page_size: 40 } }),
      api.get("/product-images/enrich/job").catch(() => ({ data: { job: null } })),
      api.get("/product-images/source-health").catch(() => ({ data: null })),
      api.get("/product-images/brand-domains").catch(() => ({ data: null })),
    ]).then(([o, p, j, h, d]) => {
      setOv(o.data); setRows(p.data.items || []); setJob(j.data.job);
      setHealth(h.data); setDomains(d.data);
    })
      .catch(() => toast.error("Failed to load image data"))
      .finally(() => setLoading(false));
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  // Poll job progress while a bulk run is active.
  useEffect(() => {
    if (job?.status !== "RUNNING") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/product-images/enrich/job");
        setJob(data.job);
        if (data.job?.status !== "RUNNING") { clearInterval(t); load(); }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(t);
  }, [job?.status, load]);

  const startBulk = async () => {
    try {
      const { data } = await api.post("/product-images/enrich/bulk");
      if (data.already_running) toast.info("Enrichment already running");
      else toast.success("Full catalogue enrichment started");
      const { data: j } = await api.get("/product-images/enrich/job");
      setJob(j.job);
    } catch { toast.error("Could not start enrichment"); }
  };

  const runAudit = async (apply) => {
    setAuditing(true);
    try {
      const { data } = await api.post("/product-images/enrich", { dry_run: !apply });
      toast.success(apply
        ? `Applied ${data.applied} verified matches · ${data.missing} still missing`
        : `Audit: ${data.auto_matched} matchable · ${data.missing} missing · ${data.verified} verified`);
      load();
    } catch { toast.error("Enrichment failed"); }
    finally { setAuditing(false); }
  };

  const doUpload = async (p, file) => {
    setBusy(p.id);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/product-images/${p.id}/upload`, fd);
      toast.success(`Image processed (${data.quality})` + (data.warnings?.length ? ` — ${data.warnings[0]}` : ""));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); }
  };

  const doRemove = async (p) => {
    try { await api.post(`/product-images/${p.id}/remove`); toast.success("Image removed"); load(); }
    catch { toast.error("Remove failed"); }
  };

  const doSetUrl = async (p) => {
    const url = window.prompt("Paste an approved image URL (OEM / authorised source):");
    if (!url) return;
    try { await api.post(`/product-images/${p.id}/set-url`, { url }); toast.success("Image set"); load(); }
    catch { toast.error("Could not set image"); }
  };

  const doResolve = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/product-images/${p.id}/resolve`);
      if (data.outcome === "AUTO_MATCHED") toast.success(`Official image matched via ${data.provider}`);
      else if (data.outcome === "REVIEW") toast.info(`Candidate found (${data.match_confidence}) — flagged for review`);
      else toast.info("No exact authorised image found — professional placeholder kept");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
    finally { setBusy(null); }
  };

  const doAiGenerate = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/image-ai/product/${p.id}/generate`);
      toast.success("AI image generated — flagged AI-generated (representative)");
      void data;
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI generation failed"); }
    finally { setBusy(null); }
  };

  const doCleanBg = async (p, file) => {
    setBusy(p.id);
    const beforeUrl = URL.createObjectURL(file);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/image-ai/product/${p.id}/clean-background`, fd);
      const afterRaw = data.image_display_url;
      const afterUrl = afterRaw ? (afterRaw.startsWith("http") ? afterRaw : `${API}${afterRaw}?t=${Date.now()}`) : null;
      if (data.ai_removed) toast.success("Background removed — flagged for review");
      else toast.info(data.message || "Original saved for review");
      setBgResult({ product: p, beforeUrl, afterUrl, aiRemoved: data.ai_removed, note: data.note });
      load();
    } catch (e) {
      URL.revokeObjectURL(beforeUrl);
      toast.error(e?.response?.data?.detail || "Background removal failed");
    } finally { setBusy(null); }
  };

  const closeBgResult = () => {
    if (bgResult?.beforeUrl) URL.revokeObjectURL(bgResult.beforeUrl);
    setBgResult(null);
  };

  const saveDomain = async (r, domain) => {
    try {
      await api.patch(`/product-images/brand-domains/${r.id}`, { official_domain: domain, confirmed: true });
      toast.success(`${r.brand} domain confirmed`);
      const { data } = await api.get("/product-images/brand-domains");
      setDomains(data);
      const { data: h } = await api.get("/product-images/source-health");
      setHealth(h);
    } catch { toast.error("Could not save domain"); }
  };

  const suggestDomain = async (r) => {
    setDomBusy(r.id);
    try {
      const { data } = await api.post(`/product-images/brand-domains/${r.id}/suggest`);
      toast.success(`AI suggests ${data.suggested_domain} (${data.confidence})`);
      const { data: d } = await api.get("/product-images/brand-domains");
      setDomains(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI suggestion failed"); }
    finally { setDomBusy(null); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="image-enrichment">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-900 text-white grid place-items-center">
            <ImageIcon className="h-6 w-6" strokeWidth={1.6} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Product Image Enrichment</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Upload any image — auto-processed to every size. Governed OEM matching · never by name alone · never AI-invented.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => runAudit(false)} disabled={auditing} data-testid="img-dryrun">
            {auditing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScanSearch className="h-4 w-4 mr-2" />}
            Run Audit
          </Button>
          <Button onClick={() => runAudit(true)} disabled={auditing} data-testid="img-apply"
            className="bg-slate-900 hover:bg-slate-800 text-white">
            <Sparkles className="h-4 w-4 mr-2" /> Apply Matches
          </Button>
          <Button onClick={startBulk} disabled={job?.status === "RUNNING"} data-testid="img-bulk"
            className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <UploadCloud className="h-4 w-4 mr-2" /> Complete All Missing
          </Button>
        </div>
      </div>

      {job && (
        <div data-testid="img-job" className="rounded-xl border border-slate-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-slate-800">
              {job.status === "RUNNING" ? "Enriching catalogue…" : `Enrichment ${job.status?.toLowerCase()}`}
            </span>
            <span className="text-slate-500 text-xs" data-testid="img-job-counts">
              {job.processed || 0} / {job.total || 0} · matched {job.matched || 0} · pending {job.missing || 0} · failed {job.failed || 0}
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={"h-full transition-all " + (job.status === "RUNNING" ? "bg-sky-500" : "bg-emerald-500")}
              style={{ width: `${job.total ? Math.round((job.processed / job.total) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat testId="stat-total" icon={ImageIcon} label="Products" value={ov?.total} tone="bg-slate-900 text-white" />
        <Stat testId="stat-coverage" icon={ShieldCheck} label="Visual Coverage"
          value={ov ? `${ov.visual_coverage_pct}%` : "—"} tone="bg-emerald-600 text-white" />
        <Stat testId="stat-verified" icon={CheckCircle2} label="Verified" value={ov?.verified} tone="bg-emerald-100 text-emerald-700" />
        <Stat testId="stat-auto" icon={Sparkles} label="Auto-Matched" value={ov?.auto_matched} tone="bg-sky-100 text-sky-700" />
        <Stat testId="stat-placeholder" icon={ImageIcon} label="Placeholder" value={ov?.placeholder} tone="bg-slate-100 text-slate-600" />
        <Stat testId="stat-missing" icon={ImageOff} label="Missing" value={ov?.missing} tone="bg-slate-100 text-slate-500" />
      </div>

      <SourceHealth health={health} />
      <BrandDomains domains={domains} onSave={saveDomain} onSuggest={suggestDomain} busyId={domBusy} />

      <div className="rounded-xl border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
        <Input data-testid="img-search" value={q} onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[200px]" placeholder="Search name, OEM part #, brand…" />
        {["", "MISSING", "AUTO_MATCHED", "AI_GENERATED", "REVIEW", "VERIFIED"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`img-filter-${s || "all"}`}
            className={"text-xs px-3 py-1.5 rounded-full border transition-colors " +
              (status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="img-grid">
          {rows.map((p) => (
            <ProductCard key={p.id} p={p} busy={busy}
              onUpload={doUpload} onRemove={doRemove} onSetUrl={doSetUrl} onResolve={doResolve}
              onAiGenerate={doAiGenerate} onCleanBg={doCleanBg} />
          ))}
          {rows.length === 0 && <div className="col-span-full py-12 text-center text-slate-400">No products match.</div>}
        </div>
      )}

      {bgResult && (
        <div data-testid="bgclean-modal"
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={closeBgResult}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Eraser className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-semibold text-slate-800">AI Background Removal</span>
                <Badge className={"text-[10px] border " + STATUS_STYLE.REVIEW}>REVIEW</Badge>
              </div>
              <button onClick={closeBgResult} data-testid="bgclean-close" className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5">
              <div className="text-[13px] font-medium text-slate-800 line-clamp-1 mb-3">{bgResult.product?.product_name}</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">Before</div>
                  <div className="aspect-square rounded-lg border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
                    <img src={bgResult.beforeUrl} alt="before" className="w-full h-full object-contain" data-testid="bgclean-before" />
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">After {bgResult.aiRemoved ? "· AI cleaned" : "· original (AI unavailable)"}</div>
                  <div className="aspect-square rounded-lg border border-amber-200 bg-white grid place-items-center overflow-hidden">
                    {bgResult.afterUrl
                      ? <img src={bgResult.afterUrl} alt="after" className="w-full h-full object-contain p-2" data-testid="bgclean-after" />
                      : <Placeholder label="No image" />}
                  </div>
                </div>
              </div>
              <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-4">
                {bgResult.note} — the original photo is kept in the gallery.
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="secondary" onClick={() => { const p = bgResult.product; closeBgResult(); doRemove(p); }}
                  data-testid="bgclean-undo" className="text-xs">Undo</Button>
                <Button onClick={closeBgResult} data-testid="bgclean-keep"
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs">Keep for review</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
